<template>
  <div v-if="orderedRequests.length" class="core-disclosure completed-section">
    <!-- ===============================================
         MAIN COLLAPSIBLE HEADER
         =============================================== -->
    <button
      type="button"
      class="core-disclosure-header completed-section-header"
      :aria-expanded="sectionOpen"
      @click="sectionOpen = !sectionOpen"
    >
      <span class="core-disclosure-title"> Completed Artwork Requests </span>

      <i class="material-icons core-disclosure-chevron">
        {{ sectionOpen ? "expand_less" : "expand_more" }}
      </i>
    </button>

    <!-- ===============================================
         TABLE
         =============================================== -->
    <div v-if="sectionOpen" class="core-table-wrap core-table-wrap--natural">
      <table class="highlight core-table completed-table">
        <thead>
          <tr>
            <th>Request Title</th>
            <th>Date completed</th>
          </tr>
        </thead>

        <tbody>
          <template v-for="request in orderedRequests" :key="request.id">
            <!-- =======================================
                 SUMMARY ROW
                 ======================================= -->
            <tr class="completed-row">
              <td>
                <div class="core-inline">
                  <button
                    type="button"
                    class="core-icon-button blue-grey-text text-darken-2"
                    :aria-expanded="isExpanded(request.id)"
                    :aria-label="
                      isExpanded(request.id)
                        ? `Collapse ${request.title}`
                        : `Expand ${request.title}`
                    "
                    @click="toggleRequest(request.id)"
                  >
                    <i class="material-icons">
                      {{ isExpanded(request.id) ? "expand_less" : "expand_more" }}
                    </i>
                  </button>

                  <strong>
                    {{ request.title }}
                  </strong>
                </div>
              </td>

              <td>
                {{ formatCompletedDate(request.completedAt) }}
              </td>
            </tr>

            <!-- =======================================
                 EXPANDED DETAIL ROW
                 ======================================= -->
            <tr v-if="isExpanded(request.id)" class="completed-detail-row">
              <td colspan="2">
                <div class="completed-detail">
                  <!-- REQUEST DETAILS -->
                  <div class="core-grid core-grid--4">
                    <div>
                      <span class="core-detail-label"> Requested by </span>

                      <div>
                        {{ request.requestedByEmail || "Unknown" }}
                      </div>
                    </div>

                    <div>
                      <span class="core-detail-label"> Required by </span>

                      <div>
                        {{ formatRequestDate(request.dueDate) || "—" }}
                      </div>
                    </div>

                    <div>
                      <span class="core-detail-label"> Completed by </span>

                      <div>
                        {{ request.completedByEmail || "Social Media" }}
                      </div>
                    </div>

                    <div>
                      <span class="core-detail-label"> Status </span>

                      <div>Completed</div>
                    </div>
                  </div>

                  <!-- NOTES -->
                  <div v-if="request.notes" class="completed-notes">
                    <span class="core-detail-label"> Request details </span>

                    <div class="core-note">
                      {{ request.notes }}
                    </div>
                  </div>

                  <!-- WORKFLOW -->
                  <div class="completed-workflow">
                    <span class="core-detail-label"> Workflow </span>

                    <div class="completed-workflow-items">
                      <span
                        v-for="stage in workflowStages"
                        :key="stage.key"
                        class="completed-stage"
                        :class="{
                          complete: request.workflow?.[stage.key] === true,
                        }"
                      >
                        <i class="material-icons">
                          {{
                            request.workflow?.[stage.key] === true
                              ? "check_circle"
                              : "radio_button_unchecked"
                          }}
                        </i>

                        {{ stage.label }}
                      </span>
                    </div>
                  </div>

                  <!-- ARTWORK -->
                  <div
                    v-if="requestArtworks(request).length"
                    class="completed-artwork-section"
                  >
                    <span class="core-detail-label"> Artwork </span>

                    <div class="core-media-grid core-media-grid--compact">
                      <div
                        v-for="(artwork, index) in requestArtworks(request)"
                        :key="artwork.storagePath || artwork.downloadUrl || index"
                        class="core-media-item"
                      >
                        <button
                          v-if="isImage(artwork)"
                          type="button"
                          class="core-media-thumb-button"
                          :aria-label="`View ${artwork.fileName || 'artwork'}`"
                          @click="$emit('preview', request, artwork)"
                        >
                          <img
                            :src="artwork.downloadUrl"
                            :alt="artwork.fileName || 'Artwork'"
                            class="core-media-thumb"
                          />

                          <span class="core-media-overlay"> View </span>
                        </button>

                        <a
                          v-else
                          :href="artwork.downloadUrl"
                          target="_blank"
                          rel="noopener noreferrer"
                          class="core-media-file-link"
                        >
                          <span class="core-strong"> PDF </span>

                          <span> Open file </span>
                        </a>

                        <div class="core-media-file-name">
                          {{ artwork.fileName || `Artwork ${index + 1}` }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
function timestampMilliseconds(value) {
  if (!value) {
    return 0;
  }

  if (typeof value.toMillis === "function") {
    return value.toMillis();
  }

  if (typeof value.toDate === "function") {
    return value.toDate().getTime();
  }

  const date = new Date(value);

  const milliseconds = date.getTime();

  return Number.isNaN(milliseconds) ? 0 : milliseconds;
}

function valueToDate(value) {
  if (!value) {
    return null;
  }

  if (typeof value.toDate === "function") {
    return value.toDate();
  }

  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return new Date(`${value}T00:00:00`);
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return date;
}

export default {
  name: "ArtworkCompletedTable",

  props: {
    requests: {
      type: Array,
      default: () => [],
    },

    workflowStages: {
      type: Array,
      default: () => [],
    },
  },

  emits: ["preview"],

  data() {
    return {
      sectionOpen: false,
      expandedRequestIds: [],
    };
  },

  computed: {
    orderedRequests() {
      return [...this.requests].sort(
        (first, second) =>
          timestampMilliseconds(second.completedAt) -
          timestampMilliseconds(first.completedAt)
      );
    },
  },

  methods: {
    toggleRequest(requestId) {
      if (this.expandedRequestIds.includes(requestId)) {
        this.expandedRequestIds = this.expandedRequestIds.filter(
          (id) => id !== requestId
        );

        return;
      }

      this.expandedRequestIds = [...this.expandedRequestIds, requestId];
    },

    isExpanded(requestId) {
      return this.expandedRequestIds.includes(requestId);
    },

    requestArtworks(request) {
      const artworks = Array.isArray(request?.artworks)
        ? request.artworks.filter((item) => item && item.downloadUrl)
        : [];

      if (artworks.length) {
        return artworks;
      }

      if (request?.artwork?.downloadUrl) {
        return [request.artwork];
      }

      return [];
    },

    isImage(artwork) {
      if (artwork?.contentType?.startsWith("image/")) {
        return true;
      }

      const fileName = String(artwork?.fileName || artwork?.file_name || "");

      return /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(fileName);
    },

    formatRequestDate(value) {
      const date = valueToDate(value);

      if (!date) {
        return "";
      }

      return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    },

    formatCompletedDate(value) {
      const date = valueToDate(value);

      if (!date) {
        return "—";
      }

      return date.toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
  },
};
</script>

<style scoped>
.completed-section {
  margin-top: 28px;
}

.completed-section-header:focus {
  outline: 2px solid #607d8b;
  outline-offset: -2px;
}

.completed-table thead {
  background: #455a64;
}

.completed-table thead th {
  color: #ffffff;
  font-weight: 700;
}

.completed-row {
  border-bottom: 1px solid #eeeeee;
}

.completed-detail-row > td {
  padding: 0 !important;
  background: #fafafa;
}

.completed-detail {
  padding: 22px;
  border-top: 1px solid #e0e0e0;
  border-bottom: 1px solid #e0e0e0;
}

.completed-notes {
  margin-top: 20px;
}

.completed-workflow {
  margin-top: 20px;
}

.completed-workflow-items {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 14px;
  margin-top: 8px;
}

.completed-stage {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: #9e9e9e;
  font-size: 0.88rem;
}

.completed-stage i {
  font-size: 18px;
}

.completed-stage.complete {
  color: #388e3c;
}

.completed-artwork-section {
  margin-top: 20px;
}

@media only screen and (max-width: 600px) {
  .completed-table {
    min-width: 520px;
  }

  .completed-detail {
    padding: 16px;
  }
}
</style>
